import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import './App.css';
import PrivacyPolicy from './PrivacyPolicy';

function Home() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const scrollToFeatures = () => {
    document.getElementById('features').scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToDownload = () => {
    document.getElementById('download').scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="app">
      {/* Navigation */}
      <nav className="navbar">
        <div className="nav-container">
          <div className="nav-logo">
            <Link to="/" className="logo-link">
              <span className="logo-text">Rental Timer</span>
            </Link>
          </div>
          <div className="nav-links">
            <a href="#features" onClick={scrollToFeatures}>Features</a>
            <a href="#download" onClick={scrollToDownload} className="cta-button">
              Download
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-container">
          <div className="hero-content">
            <h1 className={`hero-title ${isVisible ? 'fade-in' : ''}`}>
              Manage Your Short-Term Rentals
            </h1>
            <p className={`hero-subtitle ${isVisible ? 'fade-in' : ''}`}>
              Track work hours, manage team members, and automate tasks for your STR properties. 
              Stay organized with calendar integration, activity logging, and comprehensive reporting.
            </p>
            <div className={`hero-buttons ${isVisible ? 'fade-in' : ''}`}>
              <a 
                href="https://play.google.com/store/apps/details?id=com.second.rentals" 
                target="_blank" 
                rel="noopener noreferrer"
                className="download-button"
              >
                <svg className="play-store-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.61 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                </svg>
                Get it on Google Play
              </a>
              <a 
                href="https://apps.apple.com/us/app/rental-time-tracker/id6753695100" 
                target="_blank" 
                rel="noopener noreferrer"
                className="download-button"
              >
                <svg className="app-store-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M16.365 1.43c0 1.14-.47 2.195-1.226 2.99-.79.83-2.08 1.47-3.176 1.38-.12-1.1.49-2.24 1.26-3.02.83-.86 2.2-1.48 3.14-1.35.01.17.01.34.01.5zM20.74 17.02c-.66 1.52-.98 2.2-1.83 3.55-1.19 1.88-2.87 4.23-4.93 4.25-1.86.02-2.35-1.22-4.38-1.22-2.03 0-2.56 1.2-4.42 1.24-2.07.04-3.64-2.03-4.83-3.9C-.27 17.7-.9 13.3 1.27 10.3c1.38-1.95 3.57-3.19 5.65-3.16 2.22.04 3.62 1.28 4.36 1.28.71 0 2.44-1.58 4.12-1.35.7.03 2.68.28 3.95 2.12-.1.06-2.36 1.38-2.34 4.12.03 3.28 2.84 4.37 2.83 4.41z"/>
                </svg>
                Download on the App Store
              </a>
              <button onClick={scrollToFeatures} className="learn-more-button">
                Learn More
              </button>
            </div>
          </div>
          <div className={`hero-image ${isVisible ? 'slide-in' : ''}`}>
            <img src="/images/home.jpg" alt="Rental Timer App" className="hero-app-image" />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features">
        <div className="features-container">
          <h2 className="section-title">Everything you need to manage your rentals</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">🏠</div>
              <h3>STR Status Tracking</h3>
              <p>Track whether the property qualifies for STR loophole to gain real estate status</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">⏱️</div>
              <h3>Time Tracking</h3>
              <p>Log work hours for each property with start and end times, activity tracking, and detailed notes</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📅</div>
              <h3>Calendar Integration</h3>
              <p>Automatically create work logs from calendar events for check-ins and check-outs</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="download" className="cta-section">
        <div className="cta-container">
          <h2>Start managing your rentals today</h2>
          <p>Join property managers who are streamlining their STR operations</p>
          <a 
            href="https://play.google.com/store/apps/details?id=com.second.rentals" 
            target="_blank" 
            rel="noopener noreferrer"
            className="cta-download-button"
          >
            <svg className="play-store-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.61 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
            </svg>
            Download on Google Play
          </a>
          {" "}
          <a 
            href="https://apps.apple.com/us/app/rental-time-tracker/id6753695100" 
            target="_blank" 
            rel="noopener noreferrer"
            className="cta-download-button"
          >
            <svg className="app-store-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M16.365 1.43c0 1.14-.47 2.195-1.226 2.99-.79.83-2.08 1.47-3.176 1.38-.12-1.1.49-2.24 1.26-3.02.83-.86 2.2-1.48 3.14-1.35.01.17.01.34.01.5zM20.74 17.02c-.66 1.52-.98 2.2-1.83 3.55-1.19 1.88-2.87 4.23-4.93 4.25-1.86.02-2.35-1.22-4.38-1.22-2.03 0-2.56 1.2-4.42 1.24-2.07.04-3.64-2.03-4.83-3.9C-.27 17.7-.9 13.3 1.27 10.3c1.38-1.95 3.57-3.19 5.65-3.16 2.22.04 3.62 1.28 4.36 1.28.71 0 2.44-1.58 4.12-1.35.7.03 2.68.28 3.95 2.12-.1.06-2.36 1.38-2.34 4.12.03 3.28 2.84 4.37 2.83 4.41z"/>
            </svg>
            Download on the App Store
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-content">
            <div className="footer-section">
              <div className="footer-logo">
                <span className="logo-text">Rental Timer</span>
              </div>
              <p>Streamline your short-term rental operations with comprehensive time tracking and team management.</p>
            </div>
            <div className="footer-section">
              <h4>Contact</h4>
              <p>feedback.innings@gmail.com</p>
            </div>
            <div className="footer-section">
              <h4>Links</h4>
              <a href="https://play.google.com/store/apps/details?id=com.second.rentals" target="_blank" rel="noopener noreferrer">Google Play Store</a>
              <a href="https://apps.apple.com/us/app/rental-time-tracker/id6753695100" target="_blank" rel="noopener noreferrer">Apple App Store</a>
              <a href="/privacy-policy">Privacy Policy</a>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2024 Rental Timer. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      </Routes>
    </Router>
  );
}

export default App;
